// ABOUTME: Button component with variants, sizes and Flowbite-compatible color API
// ABOUTME: Provides label, icon, and loading state support

import type { ReactNode } from "react"
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import type { VariantProps } from "class-variance-authority"
import { CSpinnerInline } from "./CSpinnerInline"
import { cn } from "@shared/lib/utils"
import { buttonVariants } from "./CButton.variants"

export type FlowbiteColor =
  | "blue"
  | "gray"
  | "dark"
  | "light"
  | "none"
  | "failure"
  | "success"
  | "warning"
  | "primary"
  | "info"
  | "purple"
  | "cyan"
  | "green"
  | "indigo"
  | "lime"
  | "pink"
  | "red"
  | "teal"
  | "yellow"
  | "orange"

type ButtonVariant = VariantProps<typeof buttonVariants>["variant"]

interface BaseButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

type CustomProps = {
  label?: string | ReactNode
  icon?: ReactNode
  isProcessing?: boolean
  showLabelOnMobile?: boolean
  children?: ReactNode
  color?: FlowbiteColor
  pill?: boolean
  /**
   * The button may narrow past its label, which then ends in an ellipsis. Without it the label holds the button's width
   * (the base class's shrink-0), and a button narrowed anyway cuts a centred label at both ends. Labels without an icon.
   */
  truncate?: boolean
  /** Render as different element (useful when button is nested inside another button) */
  as?: "button" | "div" | "span"
  /** Typed for any element, since `as` decides whether a button, div or span receives it */
  onClick?: React.MouseEventHandler<HTMLElement>
}

export type CButtonProps = Omit<BaseButtonProps, "variant" | "pill" | "onClick"> & CustomProps

const colorToVariant: Record<FlowbiteColor, ButtonVariant> = {
  blue: "default",
  primary: "default",
  gray: "outline",
  dark: "dark",
  light: "light",
  none: "none",
  failure: "destructive",
  success: "default",
  warning: "destructive",
  info: "default",
  purple: "default",
  cyan: "default",
  green: "default",
  indigo: "default",
  lime: "default",
  pink: "default",
  red: "destructive",
  teal: "default",
  yellow: "default",
  orange: "default",
}

const ButtonInner = ({ label, icon, isProcessing, showLabelOnMobile = false, truncate = false }: CustomProps) => {
  // If label is a string, apply responsive classes. If it's a ReactNode, it handles its own visibility/spacing
  const isStringLabel = typeof label === "string"

  if (icon) {
    return (
      <div className="flex items-center">
        <span className="flex-shrink-0">
          {isProcessing ? (
            <CSpinnerInline
              loading
              className="w-4 h-4"
            />
          ) : (
            icon
          )}
        </span>
        {label &&
          (isStringLabel ? (
            <span className={showLabelOnMobile ? "ml-2" : "hidden ml-2 lg:block"}>{label}</span>
          ) : (
            label
          ))}
      </div>
    )
  }

  // min-w-0: a flex item is no narrower than its content by default, and the ellipsis needs a narrower box.
  if (truncate) {
    return (
      <span className="inline-flex min-w-0 items-center">
        <span className="truncate">{label}</span>
      </span>
    )
  }

  return <span className="inline-flex items-center">{label}</span>
}

export const CButton = React.forwardRef<HTMLButtonElement, CButtonProps>((props, ref) => {
  const {
    label,
    icon,
    isProcessing = false,
    showLabelOnMobile = false,
    truncate = false,
    children,
    color = "blue",
    pill = false,
    size = "xs",
    className,
    disabled,
    asChild = false,
    as = "button",
    onClick,
    ...restProps
  } = props

  const variant = colorToVariant[color] || "default"
  const content = label ?? children

  const computedClassName = cn(
    buttonVariants({ variant, size, pill, className }),
    truncate && "min-w-0 shrink",
    isProcessing && "btn-loading-shimmer",
    (isProcessing || disabled) && as !== "button" && "pointer-events-none opacity-50",
  )

  const buttonInner = (
    <ButtonInner
      label={content}
      icon={icon}
      isProcessing={isProcessing}
      showLabelOnMobile={showLabelOnMobile}
      truncate={truncate}
    />
  )

  if (asChild) {
    return (
      <Slot
        ref={ref}
        className={computedClassName}
        onClick={onClick}
      >
        {buttonInner}
      </Slot>
    )
  }

  // A div or a span announced as a button has to answer the keyboard itself, which the native one
  // does for free. These shapes exist for buttons sitting inside a clickable card, where a nested
  // <button> would be invalid HTML.
  // A native button ignores every event while disabled; these shapes only get CSS that stops the
  // pointer, so the flags have to be checked here as well.
  const isInactive = Boolean(isProcessing || disabled)
  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (isInactive) return
    if (event.key !== "Enter" && event.key !== " ") return
    event.preventDefault()
    onClick?.(event as unknown as React.MouseEvent<HTMLElement>)
  }

  if (as === "div") {
    return (
      <div
        role="button"
        aria-disabled={isInactive || undefined}
        tabIndex={isInactive ? -1 : 0}
        className={computedClassName}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {buttonInner}
      </div>
    )
  }

  if (as === "span") {
    return (
      <span
        role="button"
        aria-disabled={isInactive || undefined}
        tabIndex={isInactive ? -1 : 0}
        className={computedClassName}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {buttonInner}
      </span>
    )
  }

  return (
    <button
      ref={ref}
      type="button"
      disabled={isProcessing || disabled}
      className={computedClassName}
      onClick={onClick}
      {...restProps}
    >
      {buttonInner}
    </button>
  )
})

CButton.displayName = "CButton"
